/**
 * DH Builder Footer
 *
 * Fixed bottom bar for the bundle builder: step progress, summary, navigation.
 * Subscribes to <dh-bundle-builder> via PUB_SUB_EVENTS.bundleBuilderUpdate and
 * publishes navigation intents back via PUB_SUB_EVENTS.bundleBuilderNav.
 *
 * Decoupled from dh-fab. Only exists on the bundle builder page, rendered from
 * the section itself — no path detection required.
 */

if (!customElements.get('dh-builder-footer')) {
  class DHBuilderFooter extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 1;
      this.builderState = null;
      this.unsubscribe = null;
    }

    connectedCallback() {
      this.bindEvents();
      this.subscribeToBuilder();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    bindEvents() {
      // Back button
      const backBtn = this.querySelector('[data-action="back"]');
      if (backBtn) {
        backBtn.addEventListener('click', () => {
          this.navigateBuilder(this.currentStep - 1);
        });
      }

      // Step indicators — click to return to completed steps
      this.querySelectorAll('[data-footer-step]').forEach((indicator) => {
        indicator.addEventListener('click', () => {
          const step = parseInt(indicator.dataset.footerStep, 10);
          if (indicator.classList.contains('is-completed')) {
            this.navigateBuilder(step);
          }
        });
      });

      // Next / Checkout CTA
      const ctaBtn = this.querySelector('[data-action="footer-next"]');
      if (ctaBtn) {
        ctaBtn.addEventListener('click', () => {
          if (this.currentStep < 3) {
            this.navigateBuilder(this.currentStep + 1);
          } else {
            this.triggerCheckout();
          }
        });
      }
    }

    subscribeToBuilder() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      if (!PUB_SUB_EVENTS.bundleBuilderUpdate) return;

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.bundleBuilderUpdate, (data) => {
        this.builderState = data;
        this.currentStep = data.step;
        this.updateUI(data);
      });
    }

    navigateBuilder(step) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      if (!PUB_SUB_EVENTS.bundleBuilderNav) return;
      publish(PUB_SUB_EVENTS.bundleBuilderNav, { step });
    }

    triggerCheckout() {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      if (!PUB_SUB_EVENTS.bundleBuilderNav) return;
      publish(PUB_SUB_EVENTS.bundleBuilderNav, { action: 'checkout' });
    }

    updateUI(data) {
      const { step, totalPacks, total, discount, formatMoney, loading, packLabel } = data;

      // Step indicators
      this.querySelectorAll('[data-footer-step]').forEach((indicator) => {
        const indicatorStep = parseInt(indicator.dataset.footerStep, 10);
        indicator.classList.toggle('is-active', indicatorStep === step);
        indicator.classList.toggle('is-completed', indicatorStep < step);

        if (indicatorStep === step) {
          indicator.setAttribute('aria-current', 'step');
        } else {
          indicator.removeAttribute('aria-current');
        }

        const circle = indicator.querySelector('.dh-builder-footer__step-circle');
        if (circle) {
          if (indicatorStep < step) {
            circle.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          } else {
            circle.textContent = indicatorStep;
          }
        }
      });

      // Step lines
      this.querySelectorAll('.dh-builder-footer__step-line').forEach((line) => {
        const lineId = line.dataset.line;
        if (lineId === '1-2') {
          line.classList.toggle('is-filled', step >= 2);
        } else if (lineId === '2-3') {
          line.classList.toggle('is-filled', step >= 3);
        }
      });

      // Back button visibility
      const backBtn = this.querySelector('[data-action="back"]');
      if (backBtn) {
        backBtn.hidden = step === 1;
      }

      // Summary with savings
      const summaryEl = this.querySelector('.dh-builder-footer__summary');
      const summaryText = this.querySelector('.dh-builder-footer__summary-text');
      const savingsEl = this.querySelector('.dh-builder-footer__summary-savings');
      if (summaryEl && summaryText && totalPacks > 0 && formatMoney) {
        summaryEl.hidden = false;
        const label = packLabel || `pack${totalPacks !== 1 ? 's' : ''}`;
        summaryText.textContent = `${totalPacks}× ${label} · ${formatMoney(total)}`;

        if (savingsEl) {
          if (discount > 0) {
            savingsEl.textContent = `You save ${formatMoney(discount)}`;
            savingsEl.hidden = false;
          } else {
            savingsEl.hidden = true;
          }
        }
      } else if (summaryEl) {
        summaryEl.hidden = true;
      }

      // CTA
      const ctaBtn = this.querySelector('[data-action="footer-next"]');
      const ctaText = this.querySelector('.dh-builder-footer__cta-text');
      if (ctaBtn && ctaText) {
        if (loading) {
          ctaBtn.classList.add('is-loading');
          ctaBtn.disabled = true;
          return;
        }
        ctaBtn.classList.remove('is-loading');

        const hasItems = totalPacks > 0;
        const totalFormatted = formatMoney ? formatMoney(total) : '';
        const isMobile = window.innerWidth < 1200;

        if (step === 1) {
          ctaBtn.disabled = !hasItems;
          ctaText.textContent = hasItems
            ? (isMobile ? `Plan ${totalFormatted}` : 'Choose Plan')
            : 'Select Flavors';
        } else if (step === 2) {
          ctaBtn.disabled = false;
          ctaText.textContent = isMobile ? `Review ${totalFormatted}` : 'Review Order';
        } else if (step === 3) {
          ctaBtn.disabled = false;
          ctaText.textContent = isMobile ? `Checkout ${totalFormatted}` : 'Checkout';
        }

        ctaBtn.setAttribute('aria-label', `${ctaText.textContent}${totalFormatted ? ', total ' + totalFormatted : ''}`);
      }
    }
  }

  customElements.define('dh-builder-footer', DHBuilderFooter);
}
